import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Group, Rect, Text, Line, Path, Ellipse } from 'react-konva';
import { adaptImageSizes } from '../canvas/utils.js';
import Konva from 'konva';
import { isColorBright, isNotEmpty, RGB_2_HEX } from '../../lib/ep.js';
import { localizeFields } from '../builder/DynamicFields.js';

const punch_item_size = 60;	// px

const standard_board_size = 600;	// px
const default_answer_json = { winner:false, matches:[], players:[] };

const BattleCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const bg_image = props.indexed_images?.bg_image ?? false;

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const players = localizeFields(stage?.faceoff_players ?? [],props.locale);

	const players_mode = stage?.faceoff_players_mode ?? 'card';	// 'card' | 'full'
	const border_color_overwrite = stage?.faceoff_card_border_color_overwrite ?? true;
	const text_color_overwrite = stage?.faceoff_card_text_color_overwrite ?? true;
	const border_width = parseInt(stage?.faceoff_card_border_width ?? 3);
	const border_color = stage?.faceoff_card_border_color ?? 'rgba(30, 34, 170, 1)';
	const border_radius = parseInt(stage?.faceoff_card_border_radius ?? 30);
	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';
	const text_border_width = parseInt(stage?.faceoff_text_border_width ?? '0');
	const separation_border_width = parseInt(stage?.faceoff_board_border_width ?? 3);
	const separation_border_color = stage?.faceoff_board_border_color ?? 'rgba(255, 255, 255, 1)';
	const display_background_color = stage?.faceoff_display_background_color ?? false;
	const display_stars = stage?.faceoff_display_stars ?? true;
	const display_outter_border = stage?.display_outter_border ?? true;
	const autoflip_left_players = stage?.faceoff_autoflip_left_players ?? false;
	const display_progress = stage?.faceoff_display_progress ?? false;
	const display_player_text = stage?.faceoff_display_player_text ?? true;
	const progress_bar_color = stage?.faceoff_progress_bar_color ?? (props.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)');
	const progress_bar_bg_color = stage?.faceoff_progress_bar_bg_color ?? ((isColorBright(RGB_2_HEX(progress_bar_color))) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)');

	const refs = useRef([]);
	const size = useRef(standard_board_size);
	const player_1 = useRef(players[0]);
	const player_2 = useRef(players[1]);
	const in_transition = useRef(false);
	const actual_max_player = useRef(1);
	const is_playable = useRef(false);
	const player_indexes = useRef([0, 1]);
	const answers = useRef(default_answer_json);
	const start_time = useRef(false);
	const item_sizes = useRef({});
	const already_mounted = useRef(false);
	const default_right_player_index = useRef(props?.default_right_player_index ?? 1);

	const player_text_padding = 20;

	const generateItemSizes = () => {
		const player_size = size.current / 3;
		const player_text_w = size.current / 2 - 2 * player_text_padding;
		const player_x_ini = size.current / 4;
		const player_y_ini = player_size / 2 - 2 * player_text_padding;
		const player_text_y_ini = 3 * size.current / 4 - 2 * player_text_padding;

		const shadow_w = player_size * 0.9;
		const shadow_h = player_size / 8;
		const shadow_x_ini = player_x_ini + shadow_w / 2;
		const shadow_y_ini = player_y_ini + player_size / 1.8;

		item_sizes.current = {
			player_size,
			player_text_w,
			player_x_ini,
			player_y_ini,
			player_text_y_ini,
			shadow_w,
			shadow_h,
			shadow_x_ini,
			shadow_y_ini,
		};
	};

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const default_star_size = 80;
	const whole_stars_size = 150;
	const whole_stars_x = size.current / 4 + whole_stars_size / 2 * 100 / whole_stars_size;
	const whole_stars_y = size.current / 2 - whole_stars_size / 2 * 100 / whole_stars_size;

	const stars = [{
		size:default_star_size,
		x:-35,
		y:35,
	}, {
		size:60,
		x:45,
		y:-5,
	}, {
		size:50,
		x:-10,
		y:-50,
	}, {
		size:40,
		x:10,
		y:-70,
	}];

	const animateStars = (player_num) => {
		if(!display_stars) return;

		refs.current['all_stars'].setAttrs({
			x:whole_stars_x + ((player_num == 2) ? size.current / 2 : 0),
		});

		stars.map((star, s) => {
			const star_ref = refs.current[`star_${s + 1}`];
			const total_rotation = 20 - 40 * Math.random();

			star_ref.setAttrs({
				scaleX:0,
				scaleY:0,
				y:star.y + 30,
				rotation:0,
			});

			setTimeout(() => {
				star_ref.to({
					scaleX:star.size / default_star_size,
					scaleY:star.size / default_star_size,
					y:star.y - 25,
					rotation:total_rotation * 0.55,
					duration:0.5,
					easing:Konva.Easings.StrongEaseIn,
					onFinish:() => {
						star_ref.to({
							scaleX:0,
							scaleY:0,
							y:star.y - 70,
							rotation:total_rotation,
							duration:0.5,
							easing:Konva.Easings.StrongEaseOut,
						});
					},
				});
			}, (100 + s * 80) * Math.random());
		});
	};

	const applyCache = (player_num) => {
		if(players_mode == 'full') {
			if(refs.current[`player_${player_num}_full_bg`] == undefined) return;

			refs.current[`player_${player_num}_full_bg`].cache(default_cache_props);
			return;
		}

		if(refs.current[`player_${player_num}`] == undefined) return;

		refs.current[`player_${player_num}_text_all`].cache(default_cache_props);
		refs.current[`player_${player_num}`].cache(default_cache_props);
	};

	const getPlayerName = (player_obj, index) => {
		return (isNotEmpty(player_obj, 'name')) ? player_obj.name : `Option ${index}`;
	};

	const changePreviewPlayer = () => {
		if(!is_builder || is_test) return;
		if(refs.current['layer'] == undefined) return;

		const color = getColor(default_right_player_index.current + 1);

		if(players_mode == 'full') {
			refs.current[`player_2_full_fill`].setAttrs({
				fill:color,
			});

			let bg_image_data = indexed_images[`bg_image_${players[default_right_player_index.current].id}`];
			let has_bg_image = (bg_image_data != undefined) ? true : false;
			let bg_image_scale = 1;
			if(has_bg_image) {
				bg_image_scale = Math.min(1, (size.current / 2) / bg_image_data.image_w, size.current / bg_image_data.image_h);
			} else {
				bg_image_data = indexed_images[`image_${players[default_right_player_index.current].id}`];

				if(bg_image_data != undefined) has_bg_image = true;
				if(has_bg_image) {
					bg_image_scale = Math.min(1, (size.current / 2 - 3 * player_text_padding) / bg_image_data.image_w);
				}
			}

			refs.current[`player_2_full_img`].setAttrs({
				fillPatternImage:bg_image_data?.fillPatternImage ?? null,
				fillPatternScaleX:bg_image_scale,
				fillPatternScaleY:bg_image_scale,
				fillPatternOffsetX:0,
			});

			return;
		}

		refs.current[`player_2_text`].setAttrs({
			fill:color,
		});

		refs.current[`player_2_bg`].setAttrs({
			fill:color,
		});

		refs.current[`player_1`].setAttrs({
			stroke:color,
		});

		refs.current[`player_2`].setAttrs({
			stroke:color,
		});

		const image_data = indexed_images[`image_${players[default_right_player_index.current].id}`];
		const has_image = (image_data != undefined) ? true : false;
		const image_scale = (has_image) ? (item_sizes.current.player_size / Math.min(image_data.image_w, image_data.image_h)) : 1;

		refs.current[`player_2`].setAttrs({
			fillPatternImage:image_data?.fillPatternImage ?? null,
			fillPatternScaleX:image_scale,
			fillPatternScaleY:image_scale,
		});
	};

	const gameReset = (reset_player = false) => {
		is_playable.current = true;
		actual_max_player.current = 1;

		resetProgress();

		if(reset_player === true) changePreviewPlayer();

		if(players.length > 0) {
			player_1.current = players[0];
			player_2.current = players[default_right_player_index.current];

			if(refs.current[`player_1_text`] != undefined) {
				refs.current[`player_1_text`].setAttrs({
					text:getPlayerName(player_1.current, '1'),
				});

				refs.current[`player_2_text`].setAttrs({
					text:getPlayerName(player_2.current, '2'),
				});

				refs.current['layer'].draw();
			}
		}

		player_indexes.current = [0, default_right_player_index.current];
		answers.current = default_answer_json;
		generateItemSizes();
	};

	const gameInit = (reset_player) => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				gameReset(reset_player);

				setFullLoaded(true);

				setTimeout(() => {
					applyCache(0);
					applyCache(1);

					if(players_mode == 'card') {
						refs.current[`shadow_1`].cache({ ...default_cache_props, offset:30 });
						refs.current[`shadow_2`].cache({ ...default_cache_props, offset:30 });
					}

					mountBoard();
				}, 100);
			});
		} else {
			setFullLoaded(true);
		}
	};

	useEffect(() => {
		if(props.loaded_status != 'LOADED') return;

		gameInit();
	}, [
		props.loaded_status,
		props.indexed_images,
		stage.faceoff_players_mode,
		stage.faceoff_display_player_text,
	]);

	useEffect(() => {
		if(props.default_right_player_index == default_right_player_index.current) return;
		default_right_player_index.current = props.default_right_player_index;

		gameInit(true);
	}, [
		props.default_right_player_index,
	]);

	useEffect(() => {
		applyCache(0);
		applyCache(1);
	}, [
		stage.faceoff_card_border_width,
		stage.faceoff_card_border_color,
		stage.faceoff_card_text_color_overwrite,
		stage.faceoff_card_border_radius,
		stage.faceoff_text_border_width,
		stage.text_color,
	]);

	useEffect(() => {
		if(refs.current[`player_1`] != undefined) {
			const color1 = getColor(1);
			const color2 = getColor(default_right_player_index.current + 1);
	
			refs.current[`player_1`].setAttrs({
				stroke:(border_color_overwrite) ? border_color : color1,
			});

			refs.current[`player_2`].setAttrs({
				stroke:(border_color_overwrite) ? border_color : color2,
			});
		}

		applyCache(0);
		applyCache(1);
	}, [
		stage.faceoff_card_border_color_overwrite,
	]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			cb(success_game);
		},
	}), []);

	const images_adapter = (image) => (adaptImageSizes(image, punch_item_size, 300, 500));

	const indexed_images = useMemo(() => {
		const adapted = {};
		for(var img in props.indexed_images) {
			adapted[img] = images_adapter(props.indexed_images[img]);
		}
		return adapted;
	}, [ props.indexed_images ]);

	const finishGame = (player_num) => {
		is_playable.current = false;

		refs.current['separator_line'].to({
			opacity:0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		transitionEnd(player_num, () => {
			if(is_builder && !is_test) {
				already_mounted.current = false;
				mountBoard();
				return;
			}

			if(props.updateUserAnswer != undefined) props.updateUserAnswer(answers.current);
			props.onEnded(true, { faceoff:answers.current });
		});
	};
	

	const transitionEnd = (player_num, cb) => {
		const final_player_num = (player_num == 2) ? 1 : 2;

		if(players_mode == 'full') {
			refs.current[`player_${final_player_num}_full_bg`].to({
				x:size.current / 4,
				duration:0.7,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					setTimeout(() => {
						if(is_builder && !is_test) {
							refs.current[`player_${final_player_num}_full_bg`].to({
								x:size.current / 2,
								duration:0.7,
								easing:Konva.Easings.StrongEaseInOut,
								onFinish:() => {
									if(cb != undefined) cb();
								},
							});

							return;
						}

						if(cb != undefined) cb();
					}, 3000);
				},
			});
			return;
		}

		refs.current[`player_${final_player_num}_bg`].to({
			width:size.current,
			x:(final_player_num > 1) ? 0 : 0,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
		});

		if(display_player_text) {
			refs.current[`player_${final_player_num}_text_all`].to({
				x:((final_player_num == 2) ? 0 : 1) * size.current / 2 - player_text_padding,
				y:item_sizes.current.player_text_y_ini + 20,
				scaleX:1.3,
				scaleY:1.3,
				duration:0.7,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current[`shadow_${final_player_num}`].to({
			x:item_sizes.current.shadow_w / 2,
			y:item_sizes.current.shadow_y_ini + 10,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current[`player_${final_player_num}`].to({
			x:0,
			y:item_sizes.current.player_y_ini - 15,
			scaleX:1.3,
			scaleY:1.3,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					if(display_player_text) {
						refs.current[`player_${final_player_num}_text_all`].to({
							y:1.2 * size.current,
							scaleX:1,
							scaleY:1,
							opacity:0,
							duration:1,
							easing:Konva.Easings.StrongEaseInOut,
						});
					}

					refs.current[`shadow_${final_player_num}`].to({
						opacity:0,
						duration:0.7,
						easing:Konva.Easings.StrongEaseInOut,
					});

					refs.current[`player_${final_player_num}`].to({
						y:-2 * item_sizes.current.player_size - 20,
						scaleX:1,
						scaleY:1,
						rotation:360,
						duration:0.7,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(cb != undefined) cb();
						},
					});
				}, 3000);
			},
		});
	};

	const getColor = (player_num) => {
		if(isNotEmpty(players[player_num - 1], 'color')) return players[player_num - 1].color;
		
		const color = indexed_images[`image_${players[player_num - 1].id}`]?.avg_color;
		if(color != undefined) return `rgba(${color.r}, ${color.g}, ${color.b}, 1)`;

		return 'rgba(30, 34, 170, 1)';
	};

	const changePlayer = (player_num, cb) => {
		actual_max_player.current++;

		if(actual_max_player.current == players.length) {
			finishGame(player_num);
			return;
		}

		player_indexes.current[player_num - 1] = actual_max_player.current;

		const color = getColor(player_indexes.current[player_num - 1] + 1);

		const scaleX_img = (autoflip_left_players) ? 1 : ((player_num < 2) ? -1 : 1);

		if(display_player_text) {
			let text_player_color = text_color;
			if(!text_color_overwrite) {
				text_player_color = (isColorBright(RGB_2_HEX(color))) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)';
			}

			let text_player_color_rev = 'rgba(255, 255, 255, 1)';
			if(isColorBright(RGB_2_HEX(text_player_color))) {
				text_player_color_rev = 'rgba(0, 0, 0, 1)';
			}

			refs.current[`player_${player_num}_text`].setAttrs({
				text:players[actual_max_player.current]?.name ?? `Option ${actual_max_player.current + 1}`,
				fill:(text_color_overwrite) ? text_color : ((players_mode == 'card') ? color : text_player_color),
			});

			if(text_border_width > 0) {
				refs.current[`player_${player_num}_text_shadow`].setAttrs({
					text:players[actual_max_player.current]?.name ?? `Option ${actual_max_player.current + 1}`,
					fill:(players_mode == 'card') ? 'white' : text_player_color_rev,
					stroke:(players_mode == 'card') ? 'white' : text_player_color_rev,
				});
			}
		}

		if(players_mode == 'full') {
			let is_simulated = false;
			let img_size = { w:size.current / 2, h:size.current };
			let bg_image_data = indexed_images[`bg_image_${players[player_indexes.current[player_num - 1]].id}`];
			let has_bg_image = (bg_image_data != undefined) ? true : false;
			let bg_image_scale = 1;
			if(has_bg_image) {
				bg_image_scale = Math.min(1, (size.current / 2) / bg_image_data.image_w, size.current / bg_image_data.image_h);
			} else {
				bg_image_data = indexed_images[`image_${players[player_indexes.current[player_num - 1]].id}`];

				if(bg_image_data != undefined) has_bg_image = true;
				if(has_bg_image) {
					img_size.w = size.current / 2 - 3 * player_text_padding;
					img_size.h = img_size.w;
					bg_image_scale = Math.min(1, (img_size.w / bg_image_data.image_w));
					is_simulated = true;
				}
			}

			if(is_simulated) {
				refs.current[`player_${player_num}_full_fill`].setAttrs({
					fill:color,
				});
			} else {
				refs.current[`player_${player_num}_full_fill`].setAttrs({
					opacity:0,
				});
			}

			refs.current[`player_${player_num}_full_img`].setAttrs({
				width:img_size.w,
				height:img_size.h,
				fillPatternImage:bg_image_data?.fillPatternImage ?? null,
				fillPatternScaleX:scaleX_img * bg_image_scale,
				fillPatternScaleY:bg_image_scale,
				fillPatternOffsetX:(scaleX_img < 0) ? img_size.w / bg_image_scale : 0,
				x:(is_simulated) ? 1.5 * player_text_padding : 0,
			});

			applyCache(player_num);
			if(cb != undefined) cb();
			return;
		}

		const image_data = indexed_images[`image_${players[player_indexes.current[player_num - 1]].id}`];
		const has_image = (image_data != undefined) ? true : false;
		const image_scale = (has_image) ? (item_sizes.current.player_size / Math.min(image_data.image_w, image_data.image_h)) : 1;

		refs.current[`player_${player_num}`].setAttrs({
			fill:color,
			fillPatternImage:image_data?.fillPatternImage ?? null,
			fillPatternScaleX:scaleX_img * image_scale,
			fillPatternScaleY:image_scale,
			fillPriority:(has_image) ? 'pattern' : 'fill',
			fillPatternOffsetX:(scaleX_img < 0) ? item_sizes.current.player_size / image_scale : 0,
			strokeWidth:border_width / image_scale,
			stroke:(border_color_overwrite) ? border_color : color,
		});

		refs.current[`player_${player_num}_back`].setAttrs({
			fill:color,
			opacity:0,
		});

		refs.current[`player_${player_num}_bg`].setAttrs({
			fill:color,
			opacity:0,
		});

		if(display_player_text) {
			refs.current[`player_${player_num}_text_all`].setAttrs({
				opacity:0,
			});
		}

		applyCache(player_num);
		if(cb != undefined) cb();
	};

	const animateSelected = (player_num) => {
		if(players_mode == 'full') return;

		const scale_x_original = refs.current[`player_${player_num}`].attrs.scaleX;

		const duration = 0.3;

		const ini_props = {
			y:item_sizes.current.player_y_ini - 10,
			rotation:15,
			scaleX:-1.05 * scale_x_original,
			scaleY:1.2,
			easing:Konva.Easings.StrongEaseIn,
		};

		const end_props = {
			y:item_sizes.current.player_y_ini,
			rotation:0,
			scaleX:scale_x_original,
			scaleY:1,
			easing:Konva.Easings.StrongEaseOut,
		};

		refs.current[`player_${player_num}_back`].to({
			...ini_props,
			duration,
			onFinish:() => {
				refs.current[`player_${player_num}_back`].to({
					...end_props,
					duration,
				});
			},
		});

		setTimeout(() => {
			refs.current[`player_${player_num}_back`].to({
				opacity:1,
				duration:0.01,
				onFinish:() => {
					setTimeout(() => {
						refs.current[`player_${player_num}_back`].to({
							opacity:0,
							duration:0.01,
						});
					}, 0.3 * duration * 1000);
				},
			});
		}, 0.85 * duration * 1000);

		refs.current[`player_${player_num}`].to({
			...ini_props,
			duration,
			onFinish:() => {
				refs.current[`player_${player_num}`].to({
					...end_props,
					duration,
				});
			},
		});
	};

	const voteForPlayer = (player, rival) => {
		const end_time = Date.now();

		if(answers.current.players[player] == undefined) {
			answers.current.players[player] = {
				votes:0,
				matches:[],
				defeated_by:false,
				//index:player,
				player_id:players[player].id,
			};
		}

		answers.current.players[player].votes++;
		answers.current.players[player].matches.push(rival);

		answers.current.winner = players[player].id;

		if(answers.current.players[rival] == undefined) {
			answers.current.players[rival] = {
				votes:0,
				matches:[],
				defeated_by:false,
				//index:rival,
				player_id:players[rival].id,
			};
		}

		answers.current.players[rival].matches.push(player);
		answers.current.players[rival].defeated_by = players[player].id;

		answers.current.matches.push({
			id:answers.current.matches.length,
			time:(!is_builder || is_test) ? (end_time - start_time.current) / 1000 : 0,
			winner:players[player].id,
			loser:players[rival].id,
		});

		updateProgress();
	};

	const resetProgress = () => {
		if(refs.current['progress_bar'] == undefined) return;

		refs.current['progress_bar'].setAttrs({
			width:0,
		});

		refs.current['layer'].draw();
	};

	const updateProgress = () => {
		if(refs.current['progress_bar'] == undefined) return;

		const percent = (actual_max_player.current + 1) / players.length;

		refs.current['progress_bar'].to({
			width:percent * size.current,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const transitionOut = (player_num) => {
		if(players_mode == 'full') {
			refs.current[`player_${player_num}_full_bg`].to({
				rotation:90,
				duration:0.5,
				easing:Konva.Easings.StrongEaseIn,
				onFinish:() => {
					changePlayer(player_num, () => {
						setTimeout(() => {
							transitionIn(player_num);
						}, 200);
					});
				},
			});
			return;
		}

		refs.current[`shadow_${player_num}`].to({
			x:size.current + item_sizes.current.shadow_w / 2,
			scaleX:0.8,
			duration:0.5,
			easing:Konva.Easings.StrongEaseIn,
		});

		if(display_player_text) {
			refs.current[`player_${player_num}_text_all`].to({
				y:item_sizes.current.player_text_y_ini + size.current / 4 + 4 * player_text_padding,
				rotation:(player_num == 1) ? 10 : -10,
				opacity:0,
				easing:Konva.Easings.StrongEaseIn,
				duration:0.4,
			});
		}

		refs.current[`player_${player_num}_bg`].to({
			opacity:0,
			easing:Konva.Easings.StrongEaseIn,
			duration:0.5,
		});

		refs.current[`player_${player_num}`].to({
			x:size.current,
			rotation:300,
			duration:0.5,
			easing:Konva.Easings.StrongEaseIn,
			onFinish:() => {
				changePlayer(player_num, () => {
					setTimeout(() => {
						transitionIn(player_num);
					}, 100);
				});
			},
		});
	};

	const enablePickPlayer = () => {
		if(!is_builder || is_test) {
			start_time.current = Date.now();
		}

		in_transition.current = false;
	};

	const transitionIn = (player_num) => {
		if(players_mode == 'full') {
			refs.current[`player_${player_num}_full_bg`].to({
				rotation:0,
				duration:0.5,
				easing:Konva.Easings.StrongEaseOut,
				onFinish:() => {
					enablePickPlayer();
				},
			});

			return;
		}

		const color = getColor(player_num);

		if(display_player_text) {
			refs.current[`player_${player_num}_text_all`].setAttrs({
				opacity:0,
			});
		}

		refs.current[`player_${player_num}_text`].setAttrs({
			fill:(text_color_overwrite) ? text_color : color,
		});

		refs.current[`player_${player_num}`].setAttrs({
			y:item_sizes.current.player_y_ini,
			stroke:(border_color_overwrite) ? border_color : color,
			scaleX:1,
			scaleY:1,
		});

		refs.current[`shadow_${player_num}`].to({
			x:item_sizes.current.shadow_x_ini,
			y:item_sizes.current.shadow_y_ini,
			scaleX:1,
			duration:0.5,
			easing:Konva.Easings.StrongEaseOut,
		});

		refs.current[`player_${player_num}_bg`].to({
			opacity:(display_background_color) ? 0.3 : 0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseOut,
		});

		if(display_player_text) {
			refs.current[`player_${player_num}_text_all`].to({
				x:item_sizes.current.player_text_w / 2,
				y:item_sizes.current.player_text_y_ini,
				rotation:0,
				scaleX:1,
				scaleY:1,
				opacity:1,
				easing:Konva.Easings.BounceEaseOut,
				duration:0.4,
			});
		}

		refs.current[`player_${player_num}`].to({
			x:item_sizes.current.player_x_ini,
			rotation:0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				enablePickPlayer();
			},
		});
	};

	const hoverPlayer = (player_num, direction = 1) => {
		if(players_mode != 'card') return;
		if(in_transition.current === true) return;
		if(is_playable.current === false) return;

		if(direction > 0) {
			['1', '2'].map((player) => {
				refs.current[`player_${player}`].to({
					scaleX:(player != player_num) ? 0.95 : 1,
					scaleY:(player != player_num) ? 0.95 : 1,
					duration:0.2,
					easing:Konva.Easings.StrongEaseInOut,
				});
			});

			return;
		}

		refs.current[`player_${player_num}`].to({
			scaleX:1,
			scaleY:1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const moveAside = (player_num) => {
		if(in_transition.current === true) return;
		if(is_playable.current === false) return;

		in_transition.current = true;

		const other_player = (player_num == 2) ? 1 : 2;

		voteForPlayer(player_indexes.current[other_player - 1], player_indexes.current[player_num - 1]);

		animateStars(other_player);
		animateSelected(other_player);

		transitionOut(player_num);
	};

	const mountBoard = () => {
		if(already_mounted.current) return;

		refs.current['separator_line'].to({
			opacity:1,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
		});

		transitionIn(1);

		setTimeout(() => {
			transitionIn(2);

			already_mounted.current = true;
		}, 100);
	};

	const player_props = {
		width:item_sizes.current.player_size,
		height:item_sizes.current.player_size,
		offsetX:item_sizes.current.player_size / 2,
		offsetY:item_sizes.current.player_size / 2,
		x:item_sizes.current.player_x_ini,
		y:item_sizes.current.player_y_ini,
		cornerRadius:item_sizes.current.player_size * border_radius / 100 / 2,
	};

	const shadow_props = {
		width:item_sizes.current.shadow_w,
		height:item_sizes.current.shadow_h,
		fill:'black',
		offsetX:item_sizes.current.shadow_w / 2,
		offsetY:item_sizes.current.shadow_h / 2,
		x:item_sizes.current.shadow_x_ini,
		y:item_sizes.current.shadow_y_ini,
		filters:[Konva.Filters.Blur],
		blurRadius:30,
		opacity:0.2,
	};

	if(!full_loaded) return null;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const bg_image_scale = (bg_image !== false) ? (size.current / Math.min((bg_image?.image_w ?? 600), (bg_image?.image_h ?? 600))) : 1;

	const text_default_props = {
		width:item_sizes.current.player_text_w,
		align:'center',
		fontStyle:'bold',
		verticalAlign:'top',
		fontSize:37 * size.current / standard_board_size,
	};

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					fill={bg_color}
					width={size.current}
					height={size.current}
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="no-repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
				/>

				{(players_mode == 'full') && (
					<>
						{/* PLAYER FULL BACKGROUNDS - ini */}
						{['1', `${default_right_player_index.current + 1}`].map((player, p) => {
							let is_simulated = false;
							let img_size = { w:size.current / 2, h:size.current };
							let bg_image_data = indexed_images[`bg_image_${players[player_indexes.current[p]].id}`];
							let has_bg_image = (bg_image_data != undefined) ? true : false;
							let bg_image_scale = 1;
							if(has_bg_image) {
								bg_image_scale = Math.min(1, (size.current / 2) / bg_image_data.image_w, size.current / bg_image_data.image_h);
							} else {
								bg_image_data = indexed_images[`image_${players[player_indexes.current[p]].id}`];

								if(bg_image_data != undefined) has_bg_image = true;
								if(has_bg_image) {
									img_size.w = size.current / 2 - 3 * player_text_padding;
									img_size.h = img_size.w;
									bg_image_scale = Math.min(1, img_size.w / bg_image_data.image_w);
									is_simulated = true;
								}
							}

							const scaleX = (p < 1) ? -1 : 1;
							const scaleX_img = (autoflip_left_players) ? 1 : scaleX;
							
							const color = getColor(player);

							const player_obj = (p < 1) ? player_1.current : player_2.current;
							const text = getPlayerName(player_obj, player);

							let text_player_color = text_color;
							if(!text_color_overwrite) {
								text_player_color = (isColorBright(RGB_2_HEX(color))) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)';
							}

							let text_player_color_rev = 'rgba(255, 255, 255, 1)';
							if(isColorBright(RGB_2_HEX(text_player_color))) {
								text_player_color_rev = 'rgba(0, 0, 0, 1)';
							}

							return (
								<Group
									key={`player_${p + 1}_full_bg`}
									x={p * size.current / 2 + size.current / 4}
									scaleX={scaleX}
									offsetX={size.current / 4}
								>
									<Group
										ref={(node) => { refs.current[`player_${p + 1}_full_bg`] = node; }}
										x={size.current / 2}
										y={size.current}
										rotation={90}
										offsetX={size.current / 2}
										offsetY={size.current}
									>
										<Rect
											ref={(node) => { refs.current[`player_${p + 1}_full_fill`] = node; }}
											width={size.current / 2}
											height={size.current}
											opacity={(is_simulated) ? 1 : 0}
											fill={color}
										/>

										<Rect
											ref={(node) => { refs.current[`player_${p + 1}_full_img`] = node; }}
											width={img_size.w}
											height={img_size.h}
											x={(is_simulated) ? 1.5 * player_text_padding : 0}
											y={(is_simulated) ? (size.current - item_sizes.current.player_size) / 2 - 1.5 * player_text_padding : 0}
											fillPatternImage={bg_image_data?.fillPatternImage ?? null}
											fillPatternRepeat='no-repeat'
											fillPatternScaleX={scaleX_img * bg_image_scale}
											fillPatternScaleY={bg_image_scale}
											fillPatternOffsetX={(scaleX_img < 0) ? (img_size.w / bg_image_scale) : 0}
										/>

										<Group
											ref={(node) => { refs.current[`player_${p + 1}_text_all`] = node; }}
											x={size.current / 4}
											y={item_sizes.current.player_text_y_ini + 2 * player_text_padding}
											scaleX={scaleX}
											offsetX={size.current / 4}
											opacity={(display_player_text) ? 1 : 0}
										>
											{(text_border_width > 0) && (
												<Text
													ref={(node) => { refs.current[`player_${p + 1}_text_shadow`] = node; }}
													fill={text_player_color_rev}
													text={text}
													strokeWidth={text_border_width * 2}
													lineCap='round'
													lineJoin='round'
													stroke={text_player_color_rev}
													{...text_default_props}
													width={size.current / 2}
												/>
											)}

											<Text
												ref={(node) => { refs.current[`player_${p + 1}_text`] = node; }}
												text={text}
												fill={(text_color_overwrite) ? text_color : text_player_color}
												{...text_default_props}
												width={size.current / 2}
											/>
										</Group>
									</Group>
								</Group>
							)
						})}
						{/* PLAYER FULL BACKGROUNDS - end */}
					</>
				)}

				{(players_mode == 'card') && (
					<>
						{/* PLAYER BACKGROUNDS - ini */}
						{['1', `${default_right_player_index.current + 1}`].map((player, p) => {
							return (
								<Rect
									key={`player_${p + 1}_bg`}
									ref={(node) => { refs.current[`player_${p + 1}_bg`] = node; }}
									fill={getColor(player)}
									opacity={(display_background_color) ? 0.3 : 0}
									width={size.current / 2}
									height={size.current}
									x={p * size.current / 2}
								/>
							)
						})}
						{/* PLAYER BACKGROUNDS - end */}

						{/* PLAYER TEXTS - ini */}
						{['1', `${default_right_player_index.current + 1}`].map((player, p) => {
							const player_obj = (p < 1) ? player_1.current : player_2.current;

							const text = getPlayerName(player_obj, player);

							return (
								<Group
									key={`player_${p + 1}_text`}
									x={player_text_padding + p * size.current / 2}
								>
									<Group
										ref={(node) => { refs.current[`player_${p + 1}_text_all`] = node; }}
										x={item_sizes.current.player_text_w / 2}
										y={item_sizes.current.player_text_y_ini + size.current / 4 + 4 * player_text_padding}
										rotation={(p == 0) ? 10 : -10}
										offsetX={item_sizes.current.player_text_w / 2}
										opacity={0}
									>
										{(text_border_width > 0) && (
											<Text
												ref={(node) => { refs.current[`player_${p + 1}_text_shadow`] = node; }}
												fill='white'
												text={text}
												strokeWidth={text_border_width * 2}
												lineCap='round'
												lineJoin='round'
												stroke='white'
												{...text_default_props}
											/>
										)}

										<Text
											ref={(node) => { refs.current[`player_${p + 1}_text`] = node; }}
											text={text}
											fill={(text_color_overwrite) ? text_color : getColor(player)}
											{...text_default_props}
										/>
									</Group>
								</Group>
							)
						})}
						{/* PLAYER TEXTS - end */}

						{/* PLAYER IMAGE - ini */}
						{['1', `${default_right_player_index.current + 1}`].map((player, p) => {
							const image_data = indexed_images[`image_${players[player_indexes.current[p]].id}`];
							const has_image = (image_data != undefined) ? true : false;
							const image_scale = (has_image) ? (item_sizes.current.player_size / Math.min(image_data.image_w, image_data.image_h)) : 1;

							const scaleX = (p < 1) ? -1 : 1;
							const scaleX_img = (autoflip_left_players) ? 1 : scaleX;

							const color = getColor(player);

							return (
								<Group
									key={`player_${p + 1}_main`}
									x={p * size.current / 2}
									y={size.current / 2 + ((display_player_text) ? 0 : (item_sizes.current.player_size / 4))}
									scaleX={scaleX}
									offsetX={(p < 1) ? size.current / 2 : 0}
									offsetY={item_sizes.current.player_size / 2}
								>
									<Ellipse
										ref={(node) => { refs.current[`shadow_${p + 1}`] = node; }}
										{...shadow_props}
										x={size.current + item_sizes.current.shadow_w / 2}
										scaleX={0.8}
									/>

									<Rect
										ref={(node) => { refs.current[`player_${p + 1}`] = node; }}
										{...player_props}
										x={size.current}
										rotation={300}
										scaleX={scaleX}
										fill={color}
										fillPatternImage={image_data?.fillPatternImage ?? null}
										fillPatternRepeat='no-repeat'
										fillPatternScaleX={scaleX_img * image_scale}
										fillPatternScaleY={image_scale}
										fillPatternOffsetX={(scaleX_img < 0) ? item_sizes.current.player_size / image_scale : 0}
										fillPriority={(has_image) ? 'pattern' : 'fill'}
										stroke={(border_color_overwrite) ? border_color : color}
										strokeWidth={border_width / image_scale}
										opacity={1}
									/>

									<Rect
										ref={(node) => { refs.current[`player_${p + 1}_back`] = node; }}
										{...player_props}
										scaleX={scaleX}
										fill={color}
										strokeWidth={border_width}
										opacity={0}
									/>
								</Group>
							)
						})}
						{/* PLAYER IMAGE - end */}
					</>
				)}

				{/* LITTLE STARS - ini */}
				<Group
					ref={(node) => { refs.current['all_stars'] = node; }}
					offsetX={whole_stars_size / 2}
					offsetY={whole_stars_size / 2}
					scaleX={100 / whole_stars_size}
					scaleY={100 / whole_stars_size}
					x={whole_stars_x}
					y={whole_stars_y}
				>
					{stars.map((star, s) => {
						const star_scale = star.size / default_star_size;

						return (
							<Group
								key={`star_${s + 1}`}
								ref={(node) => { refs.current[`star_${s + 1}`] = node; }}
								scaleX={0}
								scaleY={0}
								offsetX={default_star_size / 2}
								offsetY={default_star_size / 2}
								x={star.x}
								y={star.y}
							>
								<Path
									data='M25.86,54.14C33.82,62.11,32.63,80,40,80s6.18-17.89,14.14-25.86S80,47.37,80,40s-17.89-6.18-25.86-14.14S47.37,0,40,0s-6.18,17.89-14.14,25.86S0,32.63,0,40S17.89,46.18,25.86,54.14z'
									fill='white'
									stroke='black'
									strokeWidth={1.5 / star_scale}
								/>
							</Group>
						)
					})}
				</Group>
				{/* LITTLE STARS - end */}

				{/* ACTIVE ZONES - ini */}
				<Group>
					<Line
						ref={(node) => { refs.current['separator_line'] = node; }}
						stroke={separation_border_color}
						strokeWidth={separation_border_width}
						points={[size.current / 2, 0, size.current / 2, size.current]}
					/>

					<Rect
						fill='white'
						opacity={0}
						width={size.current / 2}
						height={size.current}
						onMouseDown={() => moveAside(2)}	// onClick
						onTouchStart={() => moveAside(2)}	// onTap
						onMouseEnter={() => hoverPlayer(2, 1)}
						onMouseLeave={() => hoverPlayer(2, -1)}
					/>

					<Rect
						fill='white'
						opacity={0}
						width={size.current / 2}
						height={size.current}
						x={size.current / 2}
						onMouseDown={() => moveAside(1)}	// onClick
						onTouchStart={() => moveAside(1)}	// onTap
						onMouseEnter={() => hoverPlayer(1, 1)}
						onMouseLeave={() => hoverPlayer(1, -1)}
					/>
				</Group>
				{/* ACTIVE ZONES - end */}

				{/* PROGRESS - ini */}
				{(display_progress) && (
					<Group>
						<Rect
							width={size.current}
							height={5}
							fill={progress_bar_bg_color}
						/>

						<Rect
							ref={(node) => { refs.current['progress_bar'] = node; }}
							width={0}
							height={4}
							fill={progress_bar_color}
						/>
					</Group>
				)}
				{/* PROGRESS - end */}
			</Layer>
		</Stage>
	)
})

export default BattleCanvas;